import { useTranslation } from "react-i18next";
import { useIsCompact } from "@/shared/lib/useIsCompact";
import { contentFor, type Locale, type Package } from "@nh/shared";
import {
  DataTable,
  PriceCell,
  StatusBadge,
  Td,
  Th,
  Thumb,
  Tr,
} from "@/features/admin/components";
import { PackageCategoryLabel } from "./PackageCategoryLabel";
import { PackageRowActions } from "./PackageRowActions";

export interface PackagesTableProps {
  packages: readonly Package[];
  onEdit: (pkg: Package) => void;
  onArchive: (pkg: Package) => void;
  /** Id del paquete que se está archivando ahora mismo. */
  archivingId?: string | null;
}

/** El idioma del contenido que se enseña, derivado del de la interfaz. */
function contentLocale(language: string): Locale {
  return language.startsWith("en") ? "en" : "es";
}

/**
 * La tabla de paquetes.
 *
 * Las columnas están en el orden en que se decide un paquete: qué es,
 * para quién, qué lleva, cuánto se le quita, cuánto queda, cuántos
 * puntos da y si se ve. El precio va JUNTO al descuento porque uno sale
 * del otro: verlos separados obliga a recordar el porcentaje mientras se
 * busca la cifra.
 *
 * El precio de LISTA se enseña tachado al lado del que se cobra. Es la
 * comprobación de un vistazo: si el tachado no es mayor, ese paquete no
 * está descontando nada aunque diga que sí.
 */
export function PackagesTable({
  packages,
  onEdit,
  onArchive,
  archivingId = null,
}: PackagesTableProps) {
  const { t, i18n } = useTranslation();
  const locale = contentLocale(i18n.language);

  const compact = useIsCompact();

  if (compact) {
    return (
    <>
    {/*
      ── En un teléfono, TARJETAS (Anahí, 29-ago) ──────────────────────
      "En mobile tiene un scroll infinito de los lados y hacia abajo."
      Ocho columnas no caben en 390 px: la tabla medía 1015 y se
      deslizaba dentro de su marco, así que arrastrar movía a veces la
      tabla y a veces la página. Eso es lo que se siente infinito.

      Achicar columnas no arregla nada —quedan ilegibles—, y esconderlas
      deja media ficha. Cada paquete pasa a ser una tarjeta con lo mismo,
      apilado: se lee sin deslizar y desaparece el scroll anidado. Desde
      `md` vuelve la tabla, que ahí sí cabe entera.
    */}
    <ul className="grid gap-2.5 p-3">
      {packages.map((pkg) => {
        const content = contentFor(pkg.translations, locale);
        const units = pkg.items.reduce((total, item) => total + item.quantity, 0);
        return (
          <li
            key={pkg.id}
            className="rounded-2xl border border-ds-border bg-white p-3 shadow-ds-xs"
          >
            <div className="flex items-start gap-3">
              <Thumb url={pkg.imageUrl} name={content.name} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-[14px] font-semibold text-ds-ink">{content.name}</p>
                <p className="truncate font-mono text-[11px] text-ds-ink-3">{pkg.slug}</p>
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                  <PackageCategoryLabel category={pkg.category} />
                  <StatusBadge status={pkg.status} />
                </div>
              </div>
              <PackageRowActions
                pkg={pkg}
                name={content.name}
                onEdit={onEdit}
                onArchive={onArchive}
                archiving={archivingId === pkg.id}
              />
            </div>

            {/* Los números, en rejilla: en una línea se cortarían. */}
            <dl className="mt-3 grid grid-cols-2 gap-2 border-t border-ds-border pt-2.5 text-[12px]">
              <div>
                <dt className="text-ds-ink-3">{t("Precio")}</dt>
                <dd className="mt-0.5">
                  <PriceCell prices={pkg.prices} compareAtPrices={pkg.listPrices} />
                </dd>
              </div>
              <div>
                <dt className="text-ds-ink-3">{t("Descuento")}</dt>
                <dd className="mt-0.5 font-semibold tabular-nums text-ds-ink">
                  {pkg.discountPercent}%
                </dd>
              </div>
              <div>
                <dt className="text-ds-ink-3">{t("Productos")}</dt>
                <dd className="mt-0.5 font-semibold tabular-nums text-ds-ink">
                  {pkg.items.length}
                  <span className="ml-1 font-normal text-ds-ink-3">
                    {units === 1
                      ? t("({{n}} unidad)", { n: units })
                      : t("({{n}} unidades)", { n: units })}
                  </span>
                </dd>
              </div>
              <div>
                <dt className="text-ds-ink-3">{t("VP")}</dt>
                <dd className="mt-0.5 font-semibold tabular-nums text-ds-ink">
                  {pkg.volumePoints}
                </dd>
              </div>
            </dl>
          </li>
        );
      })}
    </ul>
    </>
    );
  }

  return (
    <DataTable caption={t("Paquetes del catálogo")}>
      <thead>
        <tr>
          <Th>{t("Paquete")}</Th>
          <Th>{t("Categoría")}</Th>
          <Th align="right">{t("Productos")}</Th>
          <Th align="right">{t("Descuento")}</Th>
          <Th align="right">{t("Precio")}</Th>
          <Th align="right">{t("VP")}</Th>
          <Th align="center">{t("Estado")}</Th>
          <Th align="right" className="w-[110px]">
            <span className="sr-only">{t("Acciones")}</span>
          </Th>
        </tr>
      </thead>
      <tbody>
        {packages.map((pkg) => {
          /*
           * `contentFor` cae al español cuando falta el inglés. Es
           * información incompleta a la vista, sí — y esa es la idea: un
           * hueco en blanco no le dice a nadie qué paquete es esa fila.
           */
          const content = contentFor(pkg.translations, locale);
          const units = pkg.items.reduce((total, item) => total + item.quantity, 0);

          return (
            <Tr key={pkg.id}>
              <Td>
                <div className="flex items-center gap-3">
                  <Thumb url={pkg.imageUrl} name={content.name} />
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-ds-ink">{content.name}</p>
                    <p className="truncate font-mono text-[11px] text-ds-ink-3">
                      {pkg.slug}
                    </p>
                  </div>
                </div>
              </Td>

              <Td>
                <PackageCategoryLabel category={pkg.category} />
              </Td>

              <Td align="right">
                <span className="font-semibold tabular-nums text-ds-ink">
                  {pkg.items.length}
                </span>
                {/*
                  Las unidades van debajo porque "3 productos" y "7
                  unidades" no son lo mismo, y en un paquete de recompra
                  la segunda cifra es la que decide el envío.
                */}
                {/*
                  El singular se elige a mano y no con la pluralización
                  de i18next: las claves de este proyecto son la frase en
                  español y el mapa del español está vacío a propósito,
                  así que `_one`/`_other` no llegan a aplicarse y
                  saldría "1 unidades".
                */}
                <span className="block text-[11px] text-ds-ink-3">
                  {units === 1
                    ? t("{{n}} unidad", { n: units })
                    : t("{{n}} unidades", { n: units })}
                </span>
              </Td>

              <Td align="right">
                <span className="font-semibold tabular-nums text-ds-ink">
                  {pkg.discountPercent}%
                </span>
              </Td>

              <Td align="right">
                <PriceCell prices={pkg.prices} compareAtPrices={pkg.listPrices} />
              </Td>

              <Td align="right">
                <span className="font-semibold tabular-nums text-ds-ink">
                  {pkg.volumePoints}
                </span>
              </Td>

              <Td align="center">
                <StatusBadge status={pkg.status} />
              </Td>

              <Td align="right">
                <PackageRowActions
                  pkg={pkg}
                  name={content.name}
                  onEdit={onEdit}
                  onArchive={onArchive}
                  archiving={archivingId === pkg.id}
                />
              </Td>
            </Tr>
          );
        })}
      </tbody>
    </DataTable>
  );
}
